import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { AlertTriangle, RotateCcw, XCircle, CheckCircle2, MinusCircle, Eraser } from 'lucide-react';
import { DTC_STATUS_BITS, hex2 } from '../../../lib/dtcDecoder/constants';
import {
  statusVerdict, statusContradictions, statusToBinary, buildStatusMaskRequest, explainMaskMatch,
  initialLifecycleState, applyLifecycleAction, LIFECYCLE_DEFAULTS,
} from '../../../lib/dtcDecoder/statusByte';
import { CopyButton, SectionLabel, VERDICT_TONE } from './shared';

const BITS_MSB_FIRST = [...DTC_STATUS_BITS].reverse();

const FORMATTERS = {
  hex: (v) => hex2(v),
  bin: (v) => statusToBinary(v),
  dec: (v) => String(v),
};
const PARSERS = {
  hex: (t) => (/^(0x)?[0-9a-f]{1,2}$/i.test(t.trim()) ? parseInt(t.trim().replace(/^0x/i, ''), 16) : null),
  bin: (t) => (/^[01]{1,8}$/.test(t.trim()) ? parseInt(t.trim(), 2) : null),
  dec: (t) => (/^\d{1,3}$/.test(t.trim()) && Number(t) <= 255 ? Number(t) : null),
};

const BitToggles = ({ value, onToggle, testPrefix }) => (
  <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5" data-testid={`${testPrefix}-toggles`}>
    {BITS_MSB_FIRST.map((b) => {
      const on = (value & b.bit) !== 0;
      return (
        <button
          key={b.index}
          type="button"
          aria-pressed={on}
          aria-label={`Bit ${b.index} ${b.name}`}
          onClick={() => onToggle(b.bit)}
          data-testid={`${testPrefix}-bit-${b.index}`}
          className={`min-h-[56px] rounded-md border px-1 py-1.5 flex flex-col items-center justify-center gap-0.5 transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${on ? 'bg-autoxium-blue text-white border-autoxium-blue' : 'bg-background border-structure hover:bg-accent'}`}
        >
          <span className="font-mono text-lg font-bold leading-none">{on ? 1 : 0}</span>
          <span className="font-mono text-[10px] leading-none opacity-80">bit {b.index}</span>
          <span className="font-mono text-[9px] leading-none opacity-80">{b.short}</span>
        </button>
      );
    })}
  </div>
);

const ACTIONS = [
  { key: 'fail', label: 'Operation cycle: test FAILS', icon: XCircle, cls: 'border-destructive/50 text-destructive hover:bg-destructive/10' },
  { key: 'pass', label: 'Operation cycle: test PASSES', icon: CheckCircle2, cls: 'border-emerald-600/50 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-600/10' },
  { key: 'notrun', label: 'Operation cycle: test NOT RUN', icon: MinusCircle, cls: '' },
  { key: 'clear', label: 'Clear DTCs (0x14)', icon: Eraser, cls: '' },
];

const NumberSetting = ({ label, value, onChange, min, max, testId }) => (
  <label className="flex items-center justify-between gap-2 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <Input
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const n = Math.round(Number(e.target.value));
        if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
      }}
      className="w-20 h-8 font-mono text-sm"
      data-testid={testId}
    />
  </label>
);

const LifecycleSimulator = ({ onLoad }) => {
  const [settings, setSettings] = useState(LIFECYCLE_DEFAULTS);
  const [history, setHistory] = useState([{ action: 'start', state: initialLifecycleState(), notes: ['Start: fault memory just cleared — status 0x50.'] }]);
  const current = history[history.length - 1];

  const act = (action) => {
    const { next, notes } = applyLifecycleAction(current.state, action, settings);
    setHistory((h) => [...h, { action, state: next, notes }].slice(-40));
  };
  const reset = () => setHistory([{ action: 'start', state: initialLifecycleState(), notes: ['Start: fault memory just cleared — status 0x50.'] }]);
  const ACTION_SHORT = { start: 'start', fail: 'FAIL', pass: 'PASS', notrun: 'not run', clear: '0x14' };

  return (
    <Card className="technical-card" data-testid="dtc-lifecycle">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">DTC Lifecycle Simulator</CardTitle>
        <CardDescription>
          Step one DTC through operation cycles and watch its status byte change. Simplified ISO 14229-1 Annex D behaviour — confirmation and ageing thresholds are OEM-defined.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3 rounded-md border border-structure p-3">
          <NumberSetting label="Confirm after N failing cycles" value={settings.confirmThreshold} min={1} max={10} onChange={(v) => setSettings((s) => ({ ...s, confirmThreshold: v }))} testId="dtc-lifecycle-confirm" />
          <NumberSetting label="Age out after N passing cycles" value={settings.ageingThreshold} min={1} max={50} onChange={(v) => setSettings((s) => ({ ...s, ageingThreshold: v }))} testId="dtc-lifecycle-ageing" />
          <NumberSetting label="Lamp off after N passing cycles" value={settings.lampOffAfter} min={1} max={10} onChange={(v) => setSettings((s) => ({ ...s, lampOffAfter: v }))} testId="dtc-lifecycle-lamp" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {ACTIONS.map((a) => (
            <Button key={a.key} variant="outline" className={`justify-start min-h-[44px] h-auto whitespace-normal text-left ${a.cls}`} onClick={() => act(a.key)} data-testid={`dtc-lifecycle-${a.key}`}>
              <a.icon className="h-4 w-4 shrink-0" /> {a.label}
            </Button>
          ))}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <SectionLabel>Timeline (tap a byte to load it in the editor)</SectionLabel>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="dtc-lifecycle-reset"><RotateCcw className="h-3.5 w-3.5" /> Reset</Button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="dtc-lifecycle-timeline">
            {history.map((h, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onLoad(h.state.status)}
                className={`rounded-md border px-2 py-1 text-center min-w-[56px] ${i === history.length - 1 ? 'border-autoxium-blue bg-autoxium-blue/10' : 'border-structure'}`}
                data-testid={`dtc-lifecycle-step-${i}`}
              >
                <div className="font-mono text-sm font-bold">{hex2(h.state.status)}</div>
                <div className="text-[9px] uppercase text-muted-foreground">{ACTION_SHORT[h.action]}</div>
              </button>
            ))}
          </div>
        </div>
        <div className={`rounded-md border px-3 py-2.5 text-sm ${VERDICT_TONE[statusVerdict(current.state.status).primary.tone]}`} data-testid="dtc-lifecycle-current">
          <p className="font-semibold">Now 0x{hex2(current.state.status)} ({statusToBinary(current.state.status)}): {statusVerdict(current.state.status).primary.title}</p>
          <ul className="list-disc pl-5 text-xs mt-1 space-y-0.5 opacity-90">
            {current.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

const MaskBuilder = ({ status }) => {
  const [mask, setMask] = useState(0x09);
  const req = buildStatusMaskRequest(mask);
  const match = explainMaskMatch(mask, status);
  return (
    <Card className="technical-card" data-testid="dtc-mask-builder">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Status Mask Builder (0x19 02)</CardTitle>
        <CardDescription>Tick the status bits you want. The ECU reports every DTC whose status byte AND the mask is non-zero — so ticking more bits returns more DTCs (it is an OR of the ticked bits, not an AND).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-2">
          {DTC_STATUS_BITS.map((b) => (
            <label key={b.index} className="flex items-center gap-2 rounded-md border border-structure px-3 py-2 min-h-[44px] cursor-pointer text-sm">
              <Checkbox checked={(mask & b.bit) !== 0} onCheckedChange={() => setMask((m) => m ^ b.bit)} data-testid={`dtc-mask-bit-${b.index}`} />
              <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">bit {b.index}</span>
              <span className="font-mono text-xs break-all">{b.name}</span>
            </label>
          ))}
        </div>
        <div className="rounded-md bg-muted/40 px-4 py-3 space-y-1">
          <SectionLabel>Request bytes</SectionLabel>
          <p className="font-mono text-xl font-bold" data-testid="dtc-mask-request">{req.map(hex2).join(' ')}</p>
          <p className="text-xs text-muted-foreground">19 = ReadDTCInformation, 02 = reportDTCByStatusMask, {hex2(mask)} = status mask ({statusToBinary(mask)}).</p>
          <p className="text-xs" data-testid="dtc-mask-match">Against the status in the editor above: <span className="font-mono">{match.text}</span></p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[[0x08, 'Confirmed only'], [0x09, 'Active or confirmed'], [0x04, 'Pending'], [0x2f, 'Typical tester'], [0xff, 'Everything']].map(([m, l]) => (
            <Badge key={m} variant="outline" className="cursor-pointer font-mono text-[11px] font-normal hover:bg-accent py-1" onClick={() => setMask(m)}>{hex2(m)} · {l}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBytePanel = ({ seed }) => {
  const [value, setValue] = useState(0x09);
  const [mode, setMode] = useState('status');
  const [drafts, setDrafts] = useState({ hex: '09', bin: '00001001', dec: '9' });
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    setDrafts((d) => {
      const next = { ...d };
      Object.keys(FORMATTERS).forEach((k) => { if (k !== editing) next[k] = FORMATTERS[k](value); });
      return next;
    });
  }, [value, editing]);

  useEffect(() => {
    if (!seed) return;
    setValue(seed.value);
    setMode(seed.mode || 'status');
  }, [seed]);

  const onDraft = (key, text) => {
    setDrafts((d) => ({ ...d, [key]: text }));
    const v = PARSERS[key](text);
    if (v !== null) setValue(v);
  };

  const verdict = statusVerdict(value);
  const issues = statusContradictions(value);
  const draftInvalid = (k) => PARSERS[k](drafts[k]) === null;

  const buildCopy = () => {
    const lines = [`${mode === 'mask' ? 'DTCStatusAvailabilityMask' : 'DTC status'}: 0x${hex2(value)} (${statusToBinary(value)}b, ${value} dec)`];
    DTC_STATUS_BITS.forEach((b) => lines.push(`bit ${b.index} ${b.name}: ${(value & b.bit) ? 1 : 0}`));
    if (mode === 'status') {
      lines.push('', `Verdict: ${verdict.primary.title}`, verdict.primary.detail);
      verdict.flags.forEach((f) => lines.push(`• ${f.title}`));
      if (issues.length) lines.push('', 'Suspicious combinations:', ...issues.map((i) => `• ${i.text}`));
    }
    return lines.join('\n');
  };

  return (
    <div className="space-y-6" data-testid="dtc-status-panel">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1.5">
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Status Byte Editor (ISO 14229-1)</CardTitle>
              <CardDescription>Tap bits or type a value — hex, binary and decimal stay in sync.</CardDescription>
            </div>
            <CopyButton getText={buildCopy} testId="dtc-status-copy" />
          </div>
          <Tabs value={mode} onValueChange={setMode} className="pt-2">
            <TabsList className="h-auto flex-wrap">
              <TabsTrigger value="status" data-testid="dtc-status-mode-status">DTC status byte</TabsTrigger>
              <TabsTrigger value="mask" data-testid="dtc-status-mode-mask">Availability mask</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[['hex', 'Hex'], ['bin', 'Binary'], ['dec', 'Decimal']].map(([k, label]) => (
              <label key={k} className="space-y-1">
                <SectionLabel>{label}</SectionLabel>
                <Input
                  className={`font-mono ${draftInvalid(k) ? 'border-destructive' : ''}`}
                  value={drafts[k]}
                  onFocus={() => setEditing(k)}
                  onBlur={() => setEditing(null)}
                  onChange={(e) => onDraft(k, e.target.value)}
                  inputMode={k === 'hex' ? 'text' : 'numeric'}
                  aria-label={`Status byte ${label}`}
                  aria-invalid={draftInvalid(k)}
                  data-testid={`dtc-status-${k}`}
                />
              </label>
            ))}
          </div>

          <BitToggles value={value} onToggle={(bit) => setValue((v) => v ^ bit)} testPrefix="dtc-status" />

          {mode === 'status' ? (
            <div className="space-y-3">
              <div className={`rounded-md border px-4 py-3 ${VERDICT_TONE[verdict.primary.tone]}`} data-testid="dtc-status-verdict">
                <p className="font-semibold">{verdict.primary.title}</p>
                <p className="text-xs mt-1 opacity-90">{verdict.primary.detail}</p>
              </div>
              {verdict.flags.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="dtc-status-flags">
                  {verdict.flags.map((f) => (
                    <Badge key={f.key} variant="outline" title={f.detail} className={`text-xs font-normal py-1 ${f.key === 'mil' ? 'border-amber-600/50 bg-amber-600/10 text-amber-800 dark:text-amber-400' : ''}`}>{f.title}</Badge>
                  ))}
                </div>
              )}
              {issues.length > 0 && (
                <div className="space-y-1.5" data-testid="dtc-status-contradictions">
                  {issues.map((i) => (
                    <div key={i.key} className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span><span className="font-semibold">Suspicious: </span>{i.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-structure px-4 py-3 text-sm" data-testid="dtc-status-mask-meaning">
              <p className="font-semibold">This ECU supports {DTC_STATUS_BITS.filter((b) => value & b.bit).length} of 8 status bits.</p>
              <p className="text-xs text-muted-foreground mt-1">The DTCStatusAvailabilityMask (byte 2 of a 59 01 / 59 02 response) lists the status bits the ECU actually implements. Unsupported bits always read 0 in every DTC&apos;s status, so a 0 there carries no information.</p>
            </div>
          )}

          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {DTC_STATUS_BITS.map((b) => {
              const on = (value & b.bit) !== 0;
              return (
                <li key={b.index} className={`flex gap-2 ${on ? '' : 'text-muted-foreground'}`}>
                  <span className={`font-mono shrink-0 w-4 text-center font-bold ${on ? 'text-autoxium-blue' : ''}`}>{on ? 1 : 0}</span>
                  <span><span className="font-mono">bit {b.index} {b.name}</span> — {mode === 'mask' ? (on ? 'supported' : 'not supported') : b.meaning}</span>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <MaskBuilder status={value} />
      <LifecycleSimulator onLoad={(v) => { setMode('status'); setValue(v); }} />
    </div>
  );
};

export default StatusBytePanel;
